import paymentsApi from '@/api/payments'
import salesApi from '@/api/sales'
import { decipherv2, encryptv2 } from '@/utils/encrypt'
import { getIVKey, removeIVKey } from '@/utils/IVKeys'

const getDefaultState = () => ({
  cardCvv: '',
  commissionAmount: 0,
  kind: '',
  identifier: '',
  id: 0,
  processed: false,
  productName: '',
  productPrice: 0,
  rationale: '',
  secureToken: null,
  status: '',
  total: 0.0,
  validToProcess: false,
  showDisplayTutorial: false,
  IVKey: null,
  email: ''
})

const state = getDefaultState()

const mutations = {
  CLEAR_STATE: (state, value) => {
    Object.assign(state, getDefaultState())
  },
  SET_CARD_CVV: (state, value) => {
    state.cardCvv = value
  },
  SET_ERROR: (state, rationale) => {
    state.rationale = rationale
    state.validToProcess = false
  },
  SET_IV_KEY: (state, value) => {
    state.IVKey = value
  },
  SET_PROCESSED_STATUS: (state, value) => {
    state.processed = value
  },
  SET_SALE_DATA: (state, data) => {
    state.commissionAmount = data.commission_amount
    state.identifier = data.guest_key
    state.email = data.guest_email
    state.guest_kind = data.guest_kind
    state.id = data.id
    state.productName = data.product_name
    state.productPrice = data.product_price
    state.rationale = data.last_attempt_status
    state.status = data.status
    state.total = data.total
    state.validToProcess = data.valid_to_process
    state.showDisplayTutorial = data.show_display_tutorial
  },
  SET_SECURE_TOKEN: (state, token) => {
    state.secureToken = token
  },
  SET_STATUS: (state, status) => {
    state.status = status
  },
  ERROR_ON_SALE: (state, rationale) => {
    state.validToProcess = false
    state.rationale = rationale
  }
}

const actions = {
  clearSaleDetailState ({ commit }) {
    commit('CLEAR_STATE')
  },
  async getSale ({ commit, state }, id) {
    try {
      const { data } = await salesApi.v2.getSale(id)
      const IVKey = getIVKey(data.id)

      commit('SET_SALE_DATA', data)
      commit('SET_IV_KEY', IVKey)
    } catch (error) {
      commit('SET_ERROR', `Ocurrio un error al procesar el pago ${state.id}. Repórtalo a la linea express`)
    }
  },
  async performPayment ({ commit, state, getters }) {
    const secureToken = encryptv2({
      ...getters.secureTokenJsonData,
      code: state.cardCvv
    }, state.IVKey.ivKey, state.identifier)
    const payment = {
      sale_order_id: state.id,
      secure_token: secureToken
    }

    try {
      const { data } = await paymentsApi.v2.performPayment({ payment })

      commit('SET_SALE_DATA', data)
      commit('SET_PROCESSED_STATUS', true)
    } catch (error) {
      if (error.isAxiosError && error.response) {
        if (error.response.status === 405) {
          const { data } = error.response

          commit('SET_SALE_DATA', data)
          commit('SET_STATUS', 'Duplicada')
          commit('SET_PROCESSED_STATUS', true)
        }
        if (error.response.status === 422) {
          const { data } = error.response

          commit('SET_ERROR', data.message)
        }
      } else {
        commit('SET_ERROR', 'Hubo un error procesando el pago. Intente nuevamente.')
      }
    }
  },
  setSecureToken ({ commit }, token) {
    commit('SET_SECURE_TOKEN', token)
  },
  updateCardCvv ({ commit }, value) {
    commit('SET_CARD_CVV', value)
  }
}

const getters = {
  isTag: (state) => state.kind === 'tag',
  identifier: (state) => state.identifier,
  rationaleMessage: (state) => state.rationale,
  cardExpirationDate: (state, getters) => {
    if (getters.secureTokenJsonData) {
      return getters.secureTokenJsonData.expires
    }

    return null
  },
  cardNumber: (state, getters) => {
    if (getters.secureTokenJsonData) {
      return getters.secureTokenJsonData.number
    }

    return null
  },
  formattedCommissionAmount: state => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'MXN',
      currencyDisplay: 'symbol'
    }).format(state.commissionAmount)
  },
  formattedNewBalance: (state, getters, rootState, rootGetters) => {
    const currentBalance = parseFloat(rootGetters.balance)
    const newBalance = parseFloat(state.productPrice) + currentBalance

    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'MXN',
      currencyDisplay: 'symbol'
    }).format(newBalance)
  },
  formattedPrice: state => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'MXN',
      currencyDisplay: 'symbol'
    }).format(state.productPrice)
  },
  formattedTotal: state => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'MXN',
      currencyDisplay: 'symbol'
    }).format(state.total)
  },
  isIVKeyValid: (state) => {
    if (!state.IVKey) {
      return false
    }

    const today = new Date()
    const keyExp = new Date(state.IVKey.ivKeyExp)

    if (today >= keyExp) {
      removeIVKey(state.IVKey.ivKey)

      return false
    }

    return true
  },
  secureTokenJsonData: state => {
    if (state.secureToken && state.IVKey) {
      return decipherv2(state.secureToken, state.IVKey.ivKey, state.identifier)
    }

    return null
  }
}

export default {
  actions,
  getters,
  mutations,
  namespaced: true,
  state
}
